import React from "react"
import { PalletStateType } from "store/production.reducer"
import Barcode from "react-barcode"

import {
  getProductName,
  getVariety,
  getLotCodes,
  getFarmRegNosArray,
  getPackType,
  getTotalBoxes,
  getTotalKgs,
  GRADE_CLASS,
} from "./labelHelpers"

interface LabelSSCCProps {
  pallet: PalletStateType
  production: any
  size?: string // To support passing size from preview
}

export default function LabelSSCC({
  pallet,
  production,
  size: selectedSize,
}: LabelSSCCProps) {
  const variety = getVariety(pallet)
  const productName = getProductName(pallet)
  const lotCodes = getLotCodes(pallet)
  const farmRegNos = getFarmRegNosArray(pallet)
  const packType = getPackType(production)
  const totalBoxes = getTotalBoxes(pallet)
  const totalKgs = getTotalKgs(pallet)
  const capacity = production?.shipmentPlan?.skuType?.capacity ?? 5
  // If we passed selectedSize (e.g., from print preview), we can use it. But in the image, Size is "L".
  const size = production?.shipmentPlan?.size ?? "L"

  const rowStyle: React.CSSProperties = {
    display: "flex",
    borderBottom: "1px solid #000",
    alignItems: "stretch", // ensure columns stretch
  }
  const labelColStyle: React.CSSProperties = {
    width: "30%",
    borderRight: "1px solid #000",
    padding: "6px 8px",
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    color: "#444",
  }
  const valueColStyle: React.CSSProperties = {
    width: "70%",
    padding: "6px 8px",
    display: "flex",
    alignItems: "center",
    fontSize: "16px",
    fontWeight: "bold",
    color: "#000",
  }

  return (
    <div
      style={{
        fontFamily: "Arial, sans-serif",
        color: "#000",
        background: "#fff",
        border: "2px solid #000",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      <div style={rowStyle}>
        <div style={labelColStyle}>Pallet No.</div>
        <div style={valueColStyle}>
          {" "}
          {pallet?.palletNumber?.slice(0, -1).slice(-6) ?? "--"}
        </div>
      </div>
      <div style={rowStyle}>
        <div style={labelColStyle}>SSCC No.</div>
        <div style={valueColStyle}>{pallet?.palletNumber ?? "--"}</div>
      </div>
      <div style={rowStyle}>
        <div style={labelColStyle}>Exporter Name</div>
        <div style={valueColStyle}>Sahyadri Farms Post Harvest Care Ltd.</div>
      </div>
      <div style={rowStyle}>
        <div style={labelColStyle}>Product</div>
        <div style={valueColStyle}>{productName}</div>
      </div>
      <div style={rowStyle}>
        <div style={labelColStyle}>Variety</div>
        <div style={valueColStyle}>{variety}</div>
      </div>
      <div style={rowStyle}>
        <div style={labelColStyle}>Pack Type</div>
        <div style={valueColStyle}>{packType}</div>
      </div>
      <div style={rowStyle}>
        <div style={labelColStyle}>Size</div>
        <div style={{ width: "70%", display: "flex" }}>
          <div
            style={{
              width: "35%",
              borderRight: "1px solid #000",
              padding: "6px 8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            {size}
          </div>
          <div
            style={{
              width: "40%",
              borderRight: "1px solid #000",
              padding: "6px 8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "#444",
            }}
          >
            Class
          </div>
          <div
            style={{
              width: "25%",
              padding: "6px 8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            {GRADE_CLASS}
          </div>
        </div>
      </div>

      {/* Box Nos / Total / Box Size - Header */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #000",
          background: "#f0f0f0",
        }}
      >
        <div
          style={{
            width: "33.33%",
            borderRight: "1px solid #000",
            padding: "4px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
            color: "#444",
          }}
        >
          Box Nos.
        </div>
        <div
          style={{
            width: "33.33%",
            borderRight: "1px solid #000",
            padding: "4px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
            color: "#444",
          }}
        >
          Total (Kgs)
        </div>
        <div
          style={{
            width: "33.33%",
            padding: "4px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
            color: "#444",
          }}
        >
          Box Size
        </div>
      </div>
      {/* Box Nos / Total / Box Size - Values */}
      <div style={{ display: "flex", borderBottom: "1px solid #000" }}>
        <div
          style={{
            width: "33.33%",
            borderRight: "1px solid #000",
            padding: "6px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {totalBoxes}
        </div>
        <div
          style={{
            width: "33.33%",
            borderRight: "1px solid #000",
            padding: "6px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {totalKgs}
        </div>
        <div
          style={{
            width: "33.33%",
            padding: "6px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {capacity}kg
        </div>
      </div>

      <div style={rowStyle}>
        <div style={labelColStyle}>Lot Code</div>
        <div style={valueColStyle}>{lotCodes.join(", ")}</div>
      </div>
      <div
        style={{
          display: "flex",
          borderBottom: "2px solid #000",
          alignItems: "stretch",
        }}
      >
        <div style={labelColStyle}>Farm Reg. No.</div>
        <div style={valueColStyle}>{farmRegNos.join(", ")}</div>
      </div>

      {/* Country of Origin */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #000",
          padding: "8px 12px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <span
          style={{ fontSize: "20px", fontWeight: "bold", letterSpacing: "1px" }}
        >
          Country Of Origin: INDIA
        </span>
      </div>

      {/* Barcode */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "12px 8px",
          width: "100%",
        }}
      >
        <Barcode
          value={`00${pallet?.palletNumber}`}
          text={`(00)${pallet?.palletNumber}`}
          width={2.2}
          height={80}
          fontSize={16}
          margin={0}
          format="CODE128"
          lineColor="#000000"
          background="transparent"
        />
      </div>
    </div>
  )
}
